import {Component, EventEmitter, Inject, Input, OnChanges, Output} from '@angular/core';
import {TuftTechnicalDatasheet} from '@domain/technical-datasheet/tuft-technical-datasheet';
import {TECHNICAL_DATASHEETS, TechnicalDatasheets} from '@infrastructure/http/technical-datasheets/technical-datasheets';
import {AlertDialogResult, BaseComponent, DialogBuilderFactoryService, DialogType, ToastService, TranslateService, WINDOW} from '@vdw/angular-component-library';
import {isNil} from 'lodash-es';
import {forkJoin, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-technical-datasheet-details',
  templateUrl: './technical-datasheet-details.component.html',
  styleUrls: ['./technical-datasheet-details.component.scss']
})
export class TechnicalDatasheetDetailsComponent extends BaseComponent implements OnChanges {
  @Input() public technicalDatasheetId: number;
  @Output() public closeDetails: EventEmitter<void> = new EventEmitter<void>();
  @Output() public technicalDatasheetDeleted: EventEmitter<TuftTechnicalDatasheet> = new EventEmitter<TuftTechnicalDatasheet>();

  public technicalDatasheet: TuftTechnicalDatasheet;
  public loading: boolean;

  private getTechnicalDatasheetByIdSubscription: Subscription;
  private readonly technicalDatasheetTranslationKey = 'TECHNICAL_DATASHEETS.DATASHEET';

  public constructor(
    @Inject(TECHNICAL_DATASHEETS) private readonly technicalDatasheets: TechnicalDatasheets,
    @Inject(WINDOW) private readonly window: Window,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService
  ) {
    super();
  }

  public ngOnChanges(): void {
    if (!isNil(this.technicalDatasheetId)) {
      this.loading = true;
      this.getTechnicalSheetDetails();
    }
  }

  public close(): void {
    this.getTechnicalDatasheetByIdSubscription?.unsubscribe();
    this.closeDetails.emit();
  }

  public openDatasheetDetails(): void {
    forkJoin([this.technicalDatasheets.getXmlForDatasheet(Number(this.technicalDatasheetId)), this.technicalDatasheets.getXSLTForDatasheet(Number(this.technicalDatasheetId))])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(([xml, xslt]: [string, string]) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'text/xml');
        const xsltDoc = parser.parseFromString(xslt, 'text/xml');
        const xsltProcessor = new XSLTProcessor();
        xsltProcessor.importStylesheet(xsltDoc);

        const result: DocumentFragment = xsltProcessor.transformToDocument(xmlDoc);

        const serializer = new XMLSerializer();
        const serializedResult = serializer.serializeToString(result);

        const newWindow = this.window.open('', '_blank');
        newWindow.document.write(serializedResult);
        newWindow.document.title = this.technicalDatasheet.name;
      });
  }

  public onDelete(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: this.technicalDatasheet.name}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((result: AlertDialogResult) => {
        if (result === AlertDialogResult.CONFIRM) {
          this.deleteTechnicalDatasheet();
        }
      });
  }

  private getTechnicalSheetDetails(): void {
    this.getTechnicalDatasheetByIdSubscription?.unsubscribe();

    this.getTechnicalDatasheetByIdSubscription = this.technicalDatasheets
      .getById(this.technicalDatasheetId)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((technicalDatasheet: TuftTechnicalDatasheet) => {
        this.technicalDatasheet = technicalDatasheet;
        this.loading = false;
      });
  }

  private deleteTechnicalDatasheet(): void {
    this.technicalDatasheets
      .delete(this.technicalDatasheet.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.toastService.showInfo({
          tapToDismiss: false,
          timeOut: 2000,
          message: this.translate.instant('GENERAL.ACTIONS.DELETED_OBJECT', {object: this.translate.instant(this.technicalDatasheetTranslationKey, {count: 1})})
        });

        this.technicalDatasheetDeleted.emit(this.technicalDatasheet);
      });
  }
}
